import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, ArrowLeft, MoreVertical, Ghost, Zap, Image as ImageIcon,
  ShieldCheck, Loader2, Info, X, CheckCircle, ChevronUp, SmilePlus, Camera, Ban, Unlock,
  Reply, Copy, Trash2, ArrowRight, Plus, Paperclip
} from 'lucide-react';
import { CameraModal } from '../components/Common/CameraModal';
import { db, auth, storage, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import { 
  doc, onSnapshot, collection, query, orderBy, limit, 
  addDoc, serverTimestamp, updateDoc, writeBatch, increment, getDoc,
  arrayUnion, arrayRemove, where, getDocs
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useIdentity } from '../context/IdentityContext';

export const ChatPage = () => {
  const { identityMode } = useIdentity();
  const { id: chatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialChatData = location.state?.chatData;
  
  const [chatData, setChatData] = useState(initialChatData);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [msgLimit, setMsgLimit] = useState(50);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [gifSearchQuery, setGifSearchQuery] = useState('');
  const [gifResults, setGifResults] = useState([]);
  const [isSearchingGif, setIsSearchingGif] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSaveRequestModal, setShowSaveRequestModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null); // Custom premium Alerts
  const [partnerTyping, setPartnerTyping] = useState(false);
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedGif, setSelectedGif] = useState(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showMobileAttachMenu, setShowMobileAttachMenu] = useState(false);
  const fileInputRef = useRef(null);
  
  // Interaction State (Mobile/Desktop Parity)
  const [replyingTo, setReplyingTo] = useState(null);
  const [contextMenuConfig, setContextMenuConfig] = useState(null);
  const touchTimerRef = useRef(null);
  
  const [showCamera, setShowCamera] = useState(false);
  
  const typingTimeoutRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const myUid = auth.currentUser?.uid;
  
  useEffect(() => {
    if (!myUid) {
      navigate('/login');
      return;
    }
    
    // Log successful entrance into the room
    logEvent(analytics, 'room_connected', { type: '1-1_text' });

    // 1. Listen to Chat Metadata
    const chatRef = doc(db, 'anonymousChats', chatId);
    const unsubChat = onSnapshot(chatRef, (docSnap) => {
      if (!docSnap.exists()) {
        navigate('/chat');
        return;
      }
      const data = docSnap.data();
      setChatData(data);
      
      // Handle Incoming Save Request
      if (data.saveStatus === 'pending' && data.saveRequestedBy !== myUid) {
          setShowSaveRequestModal(true);
      } else {
          setShowSaveRequestModal(false);
      }

      // Handle End Chat (Only show modal if the OTHER person ended the chat)
      if (data.ended && !data.saved && data.terminatedBy && data.terminatedBy !== myUid) {
          setModalConfig({
              title: "Chat Ended",
              message: "The chat has been closed.",
              buttons: [
                 { text: "Leave", onPress: () => navigate('/chat'), style: 'secondary' },
                 { text: "Next Chat", onPress: () => handleNextChat(), style: 'primary' }
              ]
          });
      }

      // Handle Typing
      const isUser1 = data.user1 === myUid;
      const pTyping = isUser1 ? data.user2Typing : data.user1Typing;
      const pLastActive = isUser1 ? data.user2LastActive : data.user1LastActive;
      
      let isStrictlyTyping = false;
      if (pTyping && pLastActive) {
          const now = Date.now();
          const lastActiveMillis = pLastActive.toMillis ? pLastActive.toMillis() : 0;
          if (now - lastActiveMillis < 5000) {
              isStrictlyTyping = true;
          }
      }
      setPartnerTyping(isStrictlyTyping);
      
      // Auto-clear unread count upon opening the chat / viewing new messages
      const myUnreadField = isUser1 ? 'unreadCount_user1' : 'unreadCount_user2';
      const myUnreadCount = data[myUnreadField] || 0;
      if (myUnreadCount > 0) {
         updateDoc(chatRef, { [myUnreadField]: 0 }).catch(() => {});
      }
    });

    // 2. Listen to Messages with Pagination
    const msgsQuery = query(
      collection(db, 'anonymousChats', chatId, 'messages'),
      orderBy('createdAt', 'desc'),
      limit(msgLimit)
    );
    
    const unsubMsgs = onSnapshot(msgsQuery, (snapshot) => {
        const msgs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date()
        })); // Notice NO reverse() here! The newest message is at index 0
        
        setMessages(msgs);
        setLoading(false);

        // Remove confirmed messages from pending
        setPendingMessages(prev => {
            return prev.filter(pMsg => !snapshot.docs.some(doc => {
                const data = doc.data();
                return data.tempId === pMsg.id || (data.text === pMsg.text && data.sender === myUid);
            }));
        });

        // Mark incoming messages as read
        const unreadMsgs = snapshot.docs.filter(d => d.data().sender !== myUid && !d.data().read);
        if (unreadMsgs.length > 0) {
            const batch = writeBatch(db);
            unreadMsgs.forEach(m => {
                batch.update(m.ref, { read: true });
            });
            batch.commit().catch(() => {});
        }
    });

    return () => {
        unsubChat();
        unsubMsgs();
    };
  }, [chatId, myUid, navigate, msgLimit]);

  const handleScroll = (e) => {
      // For flex-col-reverse, scrollTop goes negative in some browsers, positive in others. 
      // The universally safe check for hitting the "top" (oldest messages) is:
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const isAtTop = Math.abs(scrollTop) + clientHeight >= scrollHeight - 50;
      
      if (isAtTop && messages.length >= msgLimit) {
          setMsgLimit(prev => prev + 50);
      }
  };

  const isUser1 = chatData?.user1 === myUid;
  const partnerAlias = isUser1 ? chatData?.user2Alias : chatData?.user1Alias;
  const partnerCollege = isUser1 ? chatData?.user2College : chatData?.user1College;
  const isSaved = chatData?.saved === true;
  const isPending = chatData?.saveStatus === 'pending';

  let headerSubtitle = 'from the multiverse';
  if (chatData) {
      if (chatData.user1College && chatData.user2College && chatData.user1College !== chatData.user2College) {
          headerSubtitle = `from ${partnerCollege}`;
      } else if (chatData.connectionHint) {
          headerSubtitle = chatData.connectionHint.replace('Connected via', 'from');
      } else if (partnerCollege) {
          headerSubtitle = `from ${partnerCollege}`;
      }
  }

  const starterMessages = [
    'Hey, let’s kick this off, what’s on your mind? 🌱',
    'Spill the tea, who’s your secret crush? 😏',
    'What’s the juiciest rumor about you? 🕵️',
    'Yo, you single or catchin’ feelings out there? 💋',
    'Got any spicy relationship drama to drop? 🎭',
    'What’s the wildest thing in your DMs rn? 👁️',
    'What’s your biggest flex you’re hiding? 💪',
    'Review your day in one word 📝',
    'Seen any good movies lately? 🎬',
    'Hostel life or Day scholar? 🏠',
  ];

  const handleTyping = (e) => {
      setNewMessage(e.target.value);
      if (chatData?.ended) return;

      const myTypingField = isUser1 ? 'user1Typing' : 'user2Typing';
      const myLastActiveField = isUser1 ? 'user1LastActive' : 'user2LastActive';

      // Send typing status
      updateDoc(doc(db, 'anonymousChats', chatId), {
          [myTypingField]: true,
          [myLastActiveField]: serverTimestamp()
      }).catch(()=>{});

      // Clear typing after 3s of no input
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
          updateDoc(doc(db, 'anonymousChats', chatId), { [myTypingField]: false }).catch(()=>{});
      }, 3000);
  };

  const trendingGifs = [
    "https://media.giphy.com/media/l0HlOBZcl7mbVveb6/giphy.gif",
    "https://media.giphy.com/media/xT9IgG50Fb7Mi0prBC/giphy.gif",
    "https://media.giphy.com/media/26AHONQ79FdWZhAI0/giphy.gif",
    "https://media.giphy.com/media/3o7TKSjRrfIPjeiVyM/giphy.gif",
    "https://media.giphy.com/media/l41YtZOb9EUABnuqA/giphy.gif",
    "https://media.giphy.com/media/JIX9t2j0ZTN9S/giphy.gif",
    "https://media.giphy.com/media/cfuL5gqFDreXxkWQ4o/giphy.gif",
    "https://media.giphy.com/media/f31DK1KpGsyMU/giphy.gif",
  ];

  // GIF Search API
  useEffect(() => {
      if (!showGifPicker) return;
      if (gifSearchQuery.trim() === '') {
          setGifResults(trendingGifs);
          return;
      }
      const fetchGifs = async () => {
          setIsSearchingGif(true);
          try {
              const apiKey = process.env.REACT_APP_GIPHY_API_KEY || 'MkLDt7E3pR3qJnqYvjnAbGRTjq5zmQ1g';
              const res = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${encodeURIComponent(gifSearchQuery)}&limit=20`);
              if (res.ok) {
                 const data = await res.json();
                 const urls = data.data.map(r => r.images.fixed_height.url);
                 setGifResults(urls);
              } else {
                 setGifResults(trendingGifs);
              }
          } catch(e) {
              setGifResults(trendingGifs);
          } finally {
              setIsSearchingGif(false);
          }
      };
      const timeoutId = setTimeout(fetchGifs, 500); // 500ms debounce
      return () => clearTimeout(timeoutId);
  }, [gifSearchQuery, showGifPicker]);

  const handleImageClick = () => {
      fileInputRef.current?.click();
  };
  
  const handleImageChange = (e) => {
      const file = e.target.files[0];
      if (file) {
          setSelectedImage(file);
          setSelectedGif(null);
      }
  };

  const handleGifSelect = async (url) => {
      setShowGifPicker(false);
      
      if (chatData?.ended) return;

      const tempId = 'temp_' + Date.now();
      const optimisticMsg = {
          id: tempId,
          text: '',
          imageUrl: url,
          sender: myUid,
          createdAt: new Date(),
          pending: true
      };
      
      setPendingMessages(prev => [optimisticMsg, ...prev]);
      
      // Auto-scroll to bottom
      setTimeout(() => inputRef.current?.focus(), 50);

      try {
          const myTypingField = isUser1 ? 'user1Typing' : 'user2Typing';
          const myLastActiveField = isUser1 ? 'user1LastActive' : 'user2LastActive';
          const partnerUnreadField = isUser1 ? 'unreadCount_user2' : 'unreadCount_user1';

          await addDoc(collection(db, 'anonymousChats', chatId, 'messages'), {
              text: '',
              imageUrl: url,
              sender: myUid,
              tempId: tempId,
              createdAt: serverTimestamp(),
              read: false,
          });

          await updateDoc(doc(db, 'anonymousChats', chatId), {
              lastMessage: '📷 Media',
              lastMessageTime: serverTimestamp(),
              [myTypingField]: false,
              [myLastActiveField]: serverTimestamp(),
              [partnerUnreadField]: increment(1)
          });
      } catch (err) {
        //   console.error("Error sending GIF", err);
          setPendingMessages(prev => prev.filter(m => m.id !== tempId));
      }
  };
  
  const removeMedia = () => {
      setSelectedImage(null);
      setSelectedGif(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openCamera = () => { setShowCamera(true); };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if ((!newMessage.trim() && !selectedImage && !selectedGif) || chatData?.ended) return;

    const text = newMessage;
    const imgFile = selectedImage;
    const gifUrl = selectedGif;
    
    setNewMessage('');
    removeMedia();
    inputRef.current?.focus();
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    // Optimistic UI Append (unshift so it sits at the absolute bottom of flex-col-reverse)
    const tempId = 'temp_' + Date.now();
    
    // Construct Reply Payload Payload safely if it exists
    const replyPayload = replyingTo ? {
        id: replyingTo.id,
        text: replyingTo.text,
        senderAlias: replyingTo.sender === myUid ? 'Yourself' : (partnerAlias || 'Partner')
    } : null;

    const optimisticMsg = {
        id: tempId,
        text: text,
        imageUrl: imgFile ? URL.createObjectURL(imgFile) : gifUrl,
        sender: myUid,
        createdAt: new Date(),
        replyTo: replyPayload,
        pending: true
    };
    
    setPendingMessages(prev => [optimisticMsg, ...prev]);
    setReplyingTo(null);

    try {
        let imageUrl = gifUrl || null;

        if (imgFile) {
            const ext = imgFile.name.split('.').pop();
            const filename = `anonymous_chat_images/${chatId}/${Date.now()}_${myUid}.${ext}`;
            const storageRef = ref(storage, filename);
            await uploadBytesResumable(storageRef, imgFile);
            imageUrl = await getDownloadURL(storageRef);
        }

        const myTypingField = isUser1 ? 'user1Typing' : 'user2Typing';
        const myLastActiveField = isUser1 ? 'user1LastActive' : 'user2LastActive';
        const partnerUnreadField = isUser1 ? 'unreadCount_user2' : 'unreadCount_user1';

        await addDoc(collection(db, 'anonymousChats', chatId, 'messages'), {
            text,
            imageUrl,
            sender: myUid,
            tempId: tempId, // Used to clear optimistic marker safely
            ...(replyPayload && { replyTo: replyPayload }),
            createdAt: serverTimestamp(),
            read: false,
        });

        await updateDoc(doc(db, 'anonymousChats', chatId), {
            lastMessage: text || (imageUrl ? '📷 Media' : ''),
            lastMessageTime: serverTimestamp(),
            [myTypingField]: false,
            [myLastActiveField]: serverTimestamp(),
            [partnerUnreadField]: increment(1)
        });
        
    } catch (err) {
        // console.error("Error sending message", err);
        setPendingMessages(prev => prev.filter(m => m.id !== tempId));
    }
  };

  const handleEndChat = async () => {
    setModalConfig({
        title: "Leave Chat?",
        message: "Are you sure you want to end this chat? All history will be lost forever.",
        buttons: [
            { text: "Cancel", onPress: () => setModalConfig(null), style: 'secondary' },
            { 
               text: "End Chat", 
               style: 'destructive',
               onPress: async () => {
                   setModalConfig(null);
                   try {
                       await updateDoc(doc(db, 'anonymousChats', chatId), {
                           ended: true,
                           terminatedBy: myUid,
                       });
                       navigate('/chat');
                   } catch(e) {}
               }
            }
        ]
    });
  };

  const handleNextChat = async () => {
       try {
           const user = auth.currentUser;
           if (!user) return navigate('/login');

           await updateDoc(doc(db, 'anonymousChats', chatId), {
               ended: true,
               terminatedBy: myUid,
           });

           const selectedMode = location.state?.selectedMode || 'campus';
           let userCollege = location.state?.userCollege || 'Unknown';

           if (userCollege === 'Unknown') {
               const userDoc = await getDoc(doc(db, 'users', user.uid));
               if (userDoc.exists()) {
                   userCollege = userDoc.data()?.college || 'Unknown';
               }
           }

           const queueRef = collection(db, 'anonymousChatQueueV2');
           const existingQ = query(queueRef, where('uid', '==', user.uid));
           const existingSnap = await getDocs(existingQ);

           if (existingSnap.empty) {
               await addDoc(queueRef, {
                   uid: user.uid,
                   mode: selectedMode,
                   college: userCollege,
                   timestamp: serverTimestamp(),
               });

               logEvent(analytics, 'join_queue', { 
                   type: '1-1_text', 
                   initial_mode: selectedMode 
               });
           }

           navigate('/chat/searching', { replace: true, state: { selectedMode, userCollege } });
       } catch(e) {
           console.error("Next chat error:", e);
           navigate('/chat', { replace: true });
       }
  };

  const handleBackPress = () => {
    if (chatData?.ended || isSaved) {
       navigate('/chat');
    } else {
       handleEndChat();
    }
  };

  const scrollToMessage = (msgId) => {
      const element = document.getElementById(`msg-${msgId}`);
      if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Optional: Add a brief flash animation class to highlight it
          element.classList.add('bg-white/10');
          setTimeout(() => element.classList.remove('bg-white/10'), 1000);
      }
  };

  const handleSaveConnection = async () => {
    if (isSaved || isPending) return;
    try {
        await updateDoc(doc(db, 'anonymousChats', chatId), {
            saveStatus: 'pending',
            saveRequestedBy: myUid
        });
        setMenuOpen(false);
        setModalConfig({
            title: "Request Sent!",
            message: "Connection request sent! Waiting for them to accept.",
            buttons: [{ text: "Got It", onPress: () => setModalConfig(null) }]
        });
    } catch (e) {
        // console.error("Save connection failed:", e);
        setModalConfig({
            title: "Oops!",
            message: "Failed to send request: " + e.message,
            buttons: [{ text: "OK", onPress: () => setModalConfig(null) }]
        });
    }
  };

  const handleToggleBlock = async () => {
      setMenuOpen(false);
      if (!chatId || !myUid) return;
      const isBlocked = chatData?.blockedBy?.includes(myUid);
      try {
          if (isBlocked) {
              await updateDoc(doc(db, 'anonymousChats', chatId), {
                  blockedBy: arrayRemove(myUid)
              });
          } else {
              await updateDoc(doc(db, 'anonymousChats', chatId), {
                  blockedBy: arrayUnion(myUid)
              });
          }
      } catch (e) {}
  };

  const handleDeleteSavedChat = async () => {
      setMenuOpen(false);
      if (!chatId || !myUid) return;
      try {
          const fieldName = `clearedAt_${myUid}`;
          await updateDoc(doc(db, 'anonymousChats', chatId), {
              hiddenFor: arrayUnion(myUid),
              [fieldName]: serverTimestamp()
          });
          navigate('/chat');
      } catch (e) {}
  };

  // --- Context Menu Handlers (Copy, Delete) ---
  const handleContextMenu = (e, msg) => {
      e.preventDefault();
      // Only allow long-press/right-click on actual sent messages (not pending optimistic ones)
      if (msg.pending) return;

      setContextMenuConfig({
          x: e.clientX,
          y: e.clientY,
          msg
      });
  };

  const handleTouchStart = (e, msg) => {
       if (msg.pending) return;
       const touch = e.touches[0];
       touchTimerRef.current = setTimeout(() => {
           setContextMenuConfig({
               x: touch.clientX,
               y: touch.clientY,
               msg
           });
       }, 500); // 500ms Long Press
  };

  const handleTouchEnd = () => {
       if (touchTimerRef.current) clearTimeout(touchTimerRef.current);
  };
  
  const handleCopyMessage = () => {
       if (contextMenuConfig?.msg?.text) {
           navigator.clipboard.writeText(contextMenuConfig.msg.text);
       }
       setContextMenuConfig(null);
  };
  
  const handleDeleteMessage = async () => {
       const msg = contextMenuConfig?.msg;
       if (msg && msg.sender === myUid) {
           try {
               await updateDoc(doc(db, 'anonymousChats', chatId, 'messages', msg.id), {
                   text: '🚫 This message was unsent',
                   deleted: true 
               });
           } catch(e) {
            //    console.error("Failed to unsend:", e);
           }
       }
       setContextMenuConfig(null);
  };
  // ---------------------------------------------
  
  const respondToSaveRequest = async (accept) => {
      try {
          if (accept) {
              await updateDoc(doc(db, 'anonymousChats', chatId), {
                  saveStatus: 'accepted',
                  saved: true
              });
          } else {
              await updateDoc(doc(db, 'anonymousChats', chatId), {
                  saveStatus: 'none',
                  saveRequestedBy: null
              });
          }
          setShowSaveRequestModal(false);
      } catch (e) {}
  };


  if (loading) {
      return (
          <div className="h-full w-full bg-black flex items-center justify-center border-r border-gray-800">
              <Loader2 className="w-10 h-10 text-[#0BD9E5] animate-spin" />
          </div>
      );
  }

  if (identityMode === 'real') {
     return (
       <main className="w-full h-full border-r border-gray-800 flex flex-col items-center justify-center p-8 shrink-0 relative bg-[#0a0a0a]">
          <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 text-[#0BD9E5] flex items-center justify-center mb-6">
             <MessageSquare size={32} />
          </div>
          <h2 className="text-[31px] font-extrabold mb-2 leading-tight text-center">Chat</h2>
          <p className="text-gray-500 text-[15px] text-center">Coming soon for Real Identity mode.</p>
       </main>
     );
  }

  return (
    <main className="w-full h-full border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-[#050508] text-white font-poppins selection:bg-[#0BD9E5]/30">
        
       {/* Background */}
       <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(11,217,229,0.08),transparent_70%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />
       </div>

       {/* Header */}
       <div className="relative z-50 flex items-center justify-between px-4 py-4 bg-black/50 backdrop-blur-md border-b border-white/10 shrink-0">
          <div className="flex items-center gap-4">
             <button onClick={handleBackPress} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                <ArrowLeft size={20} />
             </button>
             <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center border border-[#0BD9E5] overflow-hidden">
                    {partnerAlias ? (
                        <img 
                            src={`/avatars/${partnerAlias}.png`}
                            alt={partnerAlias} 
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                        />
                    ) : null}
                    <Ghost size={20} className="text-gray-400 absolute hidden" />
                </div>
                <div>
                    <h2 className="font-bold text-lg leading-tight flex items-center gap-2">
                        {partnerAlias}
                        {isSaved && <ShieldCheck size={14} className="text-green-500" />}
                    </h2>
                    <p className="text-xs text-gray-400">{isSaved ? 'Saved Connection' : headerSubtitle}</p>
                </div>
             </div>
          </div>

          <div className="relative">
             <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                <MoreVertical size={20} />
             </button>
             
             {/* Dropdown UI */}
             <AnimatePresence>
                 {menuOpen && (
                     <motion.div 
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        className="absolute right-0 top-12 w-48 bg-[#111] border border-gray-800 rounded-xl shadow-2xl overflow-hidden z-50"
                     >
                         {!isSaved ? (
                             <>
                                 <button 
                                     onClick={handleSaveConnection}
                                     disabled={isPending}
                                     className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
                                 >
                                     <Zap size={16} className="text-[#0BD9E5]"/> {isPending ? 'Request Sent' : 'Save Connection'}
                                 </button>
                                 <button 
                                    onClick={handleNextChat}
                                    className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors flex items-center gap-2 text-sm border-t border-gray-800"
                                 >
                                    <ArrowRight size={16} /> Next Chat
                                 </button>
                                 <button 
                                    onClick={handleEndChat}
                                    className="w-full text-left px-4 py-3 hover:bg-white/5 text-red-500 transition-colors flex items-center gap-2 text-sm border-t border-gray-800"
                                 >
                                    <X size={16} /> End & Leave
                                 </button>
                             </>
                         ) : (
                             <>
                                 <button 
                                     onClick={handleToggleBlock}
                                     className="w-full text-left px-4 py-3 hover:bg-white/5 text-orange-500 transition-colors flex items-center gap-2 text-sm"
                                 >
                                     {chatData?.blockedBy?.includes(myUid) ? <Unlock size={16} /> : <Ban size={16} />}
                                     {chatData?.blockedBy?.includes(myUid) ? 'Unblock User' : 'Block User'}
                                 </button>
                                 <button 
                                     onClick={handleDeleteSavedChat}
                                     className="w-full text-left px-4 py-3 hover:bg-white/5 text-red-500 transition-colors flex items-center gap-2 text-sm border-t border-gray-800"
                                 >
                                     <Trash2 size={16} /> Delete Chat
                                 </button>
                             </>
                         )}
                     </motion.div>
                 )}
             </AnimatePresence>
          </div>
       </div>

       {/* Messages Area (Inverted via flex-col-reverse for flawless bottom-scroll anchoring like Native FlatList) */}
       <div 
           className="relative z-10 flex-1 overflow-y-auto px-4 py-6 flex flex-col-reverse gap-4 no-scrollbar border-t border-gray-800"
           onScroll={handleScroll}
       >
           <div ref={messagesEndRef} className="h-1 shrink-0" />
           
           {partnerTyping && (
               <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex justify-start w-full">
                  <div className="flex gap-2 items-end">
                     <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-[#1a1a1a] border border-gray-800 text-gray-400 flex gap-1">
                        <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0 }} className="w-1.5 h-1.5 bg-gray-500 rounded-full" />
                        <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }} className="w-1.5 h-1.5 bg-gray-500 rounded-full" />
                        <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }} className="w-1.5 h-1.5 bg-gray-500 rounded-full" />
                     </div>
                  </div>
               </motion.div>
           )}

           {[...pendingMessages, ...messages].map((msg, idx, arr) => {
               const isMe = msg.sender === myUid;

               return (
                   <motion.div 
                      key={msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex w-full shrink-0 ${isMe ? 'justify-end' : 'justify-start'}`}
                   >
                       <div 
                           className={`flex max-w-[75%] ${isMe ? 'flex-row-reverse' : 'flex-row'} gap-2 items-end`}
                           id={`msg-${msg.id}`}
                       >
                           
                           {/* Partner Avatar */}
                           {!isMe && (
                               <div className="w-6 h-6 shrink-0 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden mb-1 border border-gray-700">
                                   {partnerAlias ? (
                                       <img 
                                           src={`/avatars/${partnerAlias}.png`}
                                           alt={partnerAlias} 
                                           className="w-full h-full object-cover"
                                           onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                                       />
                                   ) : null}
                                   <Ghost size={12} className={`text-gray-500 absolute ${partnerAlias ? 'hidden' : ''}`} />
                               </div>
                           )}

                           {/* Bubble Container (Hooks Context Menu) */}
                           <div className="relative group flex items-center gap-2">
                               
                               {/* Inline Reply Button (Desktop Hover or Always-on-mobile flank) */}
                               {isMe && !msg.pending && (
                                    <button 
                                        onClick={() => { setReplyingTo(msg); inputRef.current?.focus(); }}
                                        className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-1.5 text-gray-500 hover:text-[#0BD9E5] transition-opacity rounded-full hover:bg-white/5 shrink-0"
                                    >
                                        <Reply size={16} />
                                    </button>
                               )}

                               <div 
                                   onContextMenu={(e) => handleContextMenu(e, msg)}
                                   onTouchStart={(e) => handleTouchStart(e, msg)}
                                   onTouchEnd={handleTouchEnd}
                                   onTouchMove={handleTouchEnd}
                                   className={`
                                   relative px-4 py-2.5 rounded-2xl text-sm leading-relaxed max-w-full
                                   ${isMe 
                                       ? 'bg-gradient-to-br from-[#0BD9E5] to-[#1A6C8C] text-black font-medium border border-[#0BD9E5]/50' // Sent 
                                       : 'bg-[#1a1a1a] border border-gray-800 text-gray-100' // Received
                                   }
                                   ${isMe ? 'rounded-br-sm' : 'rounded-bl-sm'}
                                   active:scale-[0.98] transition-transform cursor-pointer
                               `}>
                                   
                                   {/* Reply Attachment Preview inside Bubble */}
                                   {msg.replyTo && (
                                       <div 
                                           onClick={(e) => { e.stopPropagation(); scrollToMessage(msg.replyTo.id); }}
                                           className={`mb-2 pl-3 py-1 flex flex-col border-l-2 cursor-pointer hover:opacity-80 transition-opacity ${isMe ? 'border-black/30' : 'border-[#0BD9E5]/50'}`}
                                       >
                                           <span className={`text-[10px] font-bold uppercase tracking-wider ${isMe ? 'text-black/60' : 'text-[#0BD9E5]'}`}>
                                               {msg.replyTo.senderAlias}
                                           </span>
                                           <span className={`text-xs truncate max-w-[200px] ${isMe ? 'text-black/80' : 'text-gray-400'}`}>
                                               {msg.replyTo.text || '📷 Media'}
                                           </span>
                                       </div>
                                   )}

                                   {msg.imageUrl && (
                                       <div className="relative mb-2" style={{ minHeight: msg.pending ? '150px' : 'auto' }}>
                                           <img 
                                             src={msg.imageUrl} 
                                             alt="attachment" 
                                             className={`max-w-xs w-full h-auto rounded-xl object-cover shadow-sm ${msg.pending ? 'opacity-0' : 'opacity-100'} border border-white/10`} 
                                           />
                                           {msg.pending && (
                                               <div className="absolute inset-0 bg-[#2a2a2a] rounded-xl flex items-center justify-center overflow-hidden border border-gray-800 focus:outline-none">
                                                   <Ghost size={32} className="text-white/20 z-10" />
                                                   <motion.div 
                                                       animate={{ x: ['-100%', '100%'] }} 
                                                       transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
                                                       className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent"
                                                   />
                                               </div>
                                           )}
                                       </div>
                                   )}
                                   <span className={msg.pending ? 'opacity-80' : 'opacity-100'}>{msg.text}</span>
                               </div>

                               {/* Inline Reply Button (Desktop Hover or Always-on-mobile flank) */}
                               {!isMe && !msg.pending && (
                                    <button 
                                        onClick={() => { setReplyingTo(msg); inputRef.current?.focus(); }}
                                        className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-1.5 text-gray-500 hover:text-[#0BD9E5] transition-opacity rounded-full hover:bg-white/5 shrink-0"
                                    >
                                        <Reply size={16} />
                                    </button>
                               )}

                           </div>
                       </div>
                   </motion.div>
               )
           })}

           {messages.length === 0 && !chatData?.ended && !isSaved && (
               <div className="flex flex-col items-center justify-center my-6 py-6 w-full">
                   <p className="text-gray-500 text-xs mb-3 font-semibold tracking-widest uppercase">Break the Ice</p>
                   <div className="flex flex-wrap justify-center gap-2 max-w-md">
                       {starterMessages.map((msg, i) => (
                           <button 
                               key={i}
                               onClick={() => setNewMessage(msg)}
                               className="px-4 py-2 rounded-full border border-[#0BD9E5]/30 bg-[#0BD9E5]/5 text-[#0BD9E5] text-sm hover:bg-[#0BD9E5]/20 transition-colors"
                           >
                               {msg}
                           </button>
                       ))}
                   </div>
               </div>
           )}

           {/* Info banner - Now visually at TOP (end of flex-col-reverse) */}
           <div className="flex flex-col items-center mb-8 opacity-60 w-full py-8 mt-auto shrink-0">
               <Ghost size={32} className="mb-2 text-[#0BD9E5]" />
               <p className="text-center text-xs text-gray-400 max-w-[250px]">
                   Messages are fully encrypted. Be respectful. Treat others how you want to be treated.
               </p>
           </div>
           
       </div>           
       {/* Input Area */}
       <div className="relative z-10 p-4 bg-black/80 backdrop-blur-md border-t border-white/10 shrink-0">
           
           {(chatData?.blockedBy?.length > 0) ? (
               <div className="flex justify-center py-2">
                   <div className="bg-red-500/10 text-red-400 px-6 py-3 rounded-2xl font-semibold border border-red-500/20 text-sm flex items-center gap-2">
                       <Ban size={18} />
                       Connection Paused
                   </div>
               </div>
           ) : (
               <>
           <AnimatePresence>
               {(selectedImage || selectedGif) && (
                   <motion.div 
                       initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                       className="absolute bottom-full mb-4 left-4 p-2 bg-[#111] border border-gray-800 rounded-xl flex items-center gap-4 shadow-2xl z-50"
                   >
                       <img 
                           src={selectedImage ? URL.createObjectURL(selectedImage) : selectedGif} 
                           className="h-20 w-20 object-cover rounded-lg" 
                           alt="preview" 
                       />
                       <button type="button" onClick={removeMedia} className="p-1.5 bg-black/50 hover:bg-black border border-gray-700 rounded-full text-white transition-colors">
                           <X size={16} />
                       </button>
                   </motion.div>
               )}
           </AnimatePresence>

           {/* Simple GIF Picker */}
           <AnimatePresence>
               {showGifPicker && (
                   <motion.div 
                       initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                       className="absolute bottom-full mb-4 right-4 w-72 bg-[#111] border border-gray-800 rounded-2xl shadow-2xl overflow-hidden z-50 flex flex-col max-h-96"
                   >
                       <div className="p-3 border-b border-gray-800 flex justify-between items-center bg-black/50 backdrop-blur-md">
                           <input 
                               type="text" 
                               placeholder="Search GIFs..." 
                               value={gifSearchQuery}
                               onChange={(e) => setGifSearchQuery(e.target.value)}
                               className="bg-transparent border-none text-white text-sm focus:outline-none w-full mr-2"
                               autoFocus
                           />
                           <button onClick={() => setShowGifPicker(false)} className="text-gray-500 hover:text-white shrink-0"><X size={16}/></button>
                       </div>
                       <div className="p-2 grid grid-cols-2 gap-2 overflow-y-auto custom-scrollbar relative" style={{ minHeight: '150px', maxHeight: '250px' }}>
                          {isSearchingGif ? (
                              <div className="absolute inset-0 flex items-center justify-center">
                                  <Loader2 size={24} className="text-[#0BD9E5] animate-spin" />
                              </div>
                          ) : (
                              gifResults.map((gif, i) => (
                                  <img 
                                     key={i} src={gif} alt="gif" 
                                     onClick={() => handleGifSelect(gif)}
                                     className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity border border-gray-800"
                                  />
                              ))
                          )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

           <AnimatePresence>
               {replyingTo && (
                   <motion.div 
                       initial={{ opacity: 0, y: 10 }}
                       animate={{ opacity: 1, y: 0 }}
                       exit={{ opacity: 0, y: 10 }}
                       className="absolute bottom-full mb-2 left-0 right-0 px-2 z-40"
                   >
                       <div className="bg-[#1a1a1a] border-l-4 border-[#0BD9E5] rounded-xl p-3 flex justify-between items-center shadow-lg border-y border-r border-y-gray-800 border-r-gray-800 backdrop-blur-md">
                           <div className="flex flex-col overflow-hidden w-full">
                               <span className="text-xs font-bold text-[#0BD9E5] uppercase tracking-wider mb-0.5 flex items-center gap-1.5">
                                   <Reply size={12} />
                                   Replying to {replyingTo.senderAlias || (replyingTo.sender === myUid ? 'Yourself' : (partnerAlias || 'Partner'))}
                               </span>
                               <span className="text-sm text-gray-300 truncate pr-4">
                                   {replyingTo.text || '📷 Media Attachment'}
                               </span>
                           </div>
                           <button 
                               onClick={() => setReplyingTo(null)}
                               className="p-1.5 bg-gray-800 hover:bg-red-500/20 text-gray-400 hover:text-red-500 rounded-full transition-colors shrink-0"
                           >
                           </button>
                       </div>
                   </motion.div>
               )}
           </AnimatePresence>

            <form onSubmit={handleSendMessage} className="flex gap-2 relative z-50">
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={handleImageChange} 
                    accept="image/*" 
                    className="hidden" 
                />
                
                {/* Mobile Attach Media Menu Popup Bubble */}
                <AnimatePresence>
                    {showMobileAttachMenu && (
                        <>
                            <div 
                                className="fixed inset-0 z-40" 
                                onClick={() => setShowMobileAttachMenu(false)} 
                            />
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.9, y: 10 }}
                                className="absolute bottom-full mb-3 right-14 bg-[#111]/95 border border-gray-800 rounded-2xl p-2 shadow-2xl z-50 flex items-center gap-2 backdrop-blur-xl"
                            >
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowMobileAttachMenu(false);
                                        setShowGifPicker(!showGifPicker);
                                    }}
                                    className={`px-3 py-2 rounded-xl transition-colors font-bold text-xs border flex items-center gap-1.5 ${showGifPicker ? 'text-[#0BD9E5] border-[#0BD9E5] bg-[#0BD9E5]/10' : 'text-gray-300 hover:text-white border-gray-800 bg-black/60'}`}
                                >
                                    GIF
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowMobileAttachMenu(false);
                                        openCamera();
                                    }}
                                    className="p-2.5 text-gray-300 hover:text-white hover:bg-white/10 rounded-xl transition-colors bg-black/60 border border-gray-800 flex items-center justify-center"
                                    title="Camera"
                                >
                                    <Camera size={18} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowMobileAttachMenu(false);
                                        handleImageClick();
                                    }}
                                    className="p-2.5 text-gray-300 hover:text-white hover:bg-white/10 rounded-xl transition-colors bg-black/60 border border-gray-800 flex items-center justify-center"
                                    title="Image"
                                >
                                    <ImageIcon size={18} />
                                </button>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>

                <div className="flex-1 relative flex items-center">
                    <input
                        ref={inputRef}
                        type="text"
                        value={newMessage}
                        onChange={handleTyping}
                        placeholder="Message..."
                        className="w-full bg-[#111] border border-gray-800 rounded-full px-5 py-3 pr-12 sm:pr-28 text-sm focus:outline-none focus:border-[#0BD9E5]/50 transition-colors"
                    />
                    
                    {/* Desktop Icon Group */}
                    <div className="absolute right-2 hidden sm:flex items-center pr-1 gap-1">
                        <button 
                            type="button" 
                            onClick={() => setShowGifPicker(!showGifPicker)}
                            className={`mx-1 transition-colors font-bold text-[11px] border rounded flex items-center justify-center px-1.5 py-0.5 ${showGifPicker ? 'text-[#0BD9E5] border-[#0BD9E5] bg-[#0BD9E5]/10' : 'text-gray-500 hover:text-white border-gray-500'}`}
                        >
                            GIF
                        </button>
                        <button 
                            type="button" 
                            onClick={openCamera}
                            className="p-1.5 text-gray-500 hover:text-white transition-colors"
                        >
                            <Camera size={20} />
                        </button>
                        <button 
                            type="button" 
                            onClick={handleImageClick}
                            className="p-1.5 text-gray-500 hover:text-white transition-colors"
                        >
                            <ImageIcon size={20} />
                        </button>
                    </div>

                    {/* Mobile Single Toggle Icon Button */}
                    <div className="absolute right-2 flex sm:hidden items-center pr-1">
                        <button 
                            type="button" 
                            onClick={() => setShowMobileAttachMenu(!showMobileAttachMenu)}
                            className={`p-2 rounded-full transition-all ${showMobileAttachMenu ? 'text-[#0BD9E5] bg-[#0BD9E5]/20 rotate-45' : 'text-gray-400 hover:text-white'}`}
                        >
                            <Plus size={20} />
                        </button>
                    </div>
                </div>
                
                <button
                    type="submit"
                    disabled={!newMessage.trim() && !selectedImage && !selectedGif}
                    className="w-12 h-12 rounded-full bg-[#0BD9E5] flex items-center justify-center text-black hover:bg-white transition-colors disabled:opacity-50 disabled:hover:bg-[#0BD9E5] shrink-0"
                >
                    <Send size={18} className="translate-x-0.5" />
                </button>
            </form>
            </>
            )}
        </div>

        {/* Custom Modals */}
        <AnimatePresence>
            {modalConfig && (
                <motion.div 
                   initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                   className="absolute inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 pointer-events-auto"
                >
                    <motion.div 
                       initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                       className="bg-[#111] border border-gray-800 p-6 rounded-3xl w-full max-w-sm text-center shadow-2xl"
                    >
                        <h3 className="text-xl font-bold mb-2 text-white">{modalConfig.title}</h3>
                        <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                            {modalConfig.message}
                        </p>
                        <div className="flex gap-3 justify-center">
                            {modalConfig.buttons?.map((btn, i) => (
                                <button 
                                   key={i}
                                   onClick={btn.onPress}
                                   className={`flex-1 py-3 rounded-xl font-bold transition-all ${
                                       btn.style === 'destructive' ? 'bg-red-500 hover:bg-red-600 text-white' :
                                       btn.style === 'secondary' ? 'border border-gray-700 hover:bg-white/5 text-gray-300' :
                                       'bg-gradient-to-r from-[#0BD9E5] to-[#1A6C8C] text-black hover:opacity-90'
                                   }`}
                                >
                                   {btn.text}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>

        {/* Context Menus (Copy/Delete) */}
        <AnimatePresence>
            {contextMenuConfig && (
                <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 z-[200]" // Overlay to capture outside clicks
                    onClick={() => setContextMenuConfig(null)}
                    onContextMenu={(e) => { e.preventDefault(); setContextMenuConfig(null); }}
                >
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 10 }}
                        style={{ 
                            position: 'absolute', 
                            top: Math.min(contextMenuConfig.y, window.innerHeight - 150),
                            left: Math.min(contextMenuConfig.x, window.innerWidth - 180)
                        }}
                        className="bg-[#1a1a1a] shadow-2xl rounded-2xl border border-gray-800 p-1 w-44 overflow-hidden z-[201] flex flex-col"
                        onClick={(e) => e.stopPropagation()} // Prevent overlay click from closing immediately
                    >
                        {contextMenuConfig.msg?.text && (
                            <>
                                <button 
                                    onClick={() => { setReplyingTo(contextMenuConfig.msg); setContextMenuConfig(null); }}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-white/10 transition-colors w-full text-left text-sm text-[#0BD9E5] rounded-xl font-medium"
                                >
                                    <Reply size={16} /> Reply
                                </button>
                                <button 
                                    onClick={handleCopyMessage}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-white/10 transition-colors w-full text-left text-sm text-gray-200 rounded-xl"
                                >
                                    <Copy size={16} /> Copy
                                </button>
                            </>
                        )}
                        {contextMenuConfig.msg?.sender === myUid && (
                            <button 
                                onClick={handleDeleteMessage}
                                className="flex items-center gap-3 px-4 py-3 hover:bg-white/10 transition-colors w-full text-left text-sm text-red-500 rounded-xl mt-1"
                            >
                                <Trash2 size={16} /> Unsend
                            </button>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>

        {/* Incoming Save Request Modal */}
       <AnimatePresence>
           {showSaveRequestModal && (
               <motion.div 
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="absolute inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 pointer-events-auto"
               >
                   <motion.div 
                      className="bg-[#111] border border-[#0BD9E5]/30 p-8 rounded-3xl w-full max-w-sm text-center shadow-2xl"
                   >
                       <Zap size={40} className="text-[#0BD9E5] mx-auto mb-4" />
                       <h3 className="text-xl font-bold mb-2">Let's Connect?</h3>
                       <p className="text-gray-400 text-sm mb-6">
                           {partnerAlias} wants to save this connection. You'll be able to DM each other anytime.
                       </p>
                       <div className="flex gap-3">
                           <button 
                              onClick={() => respondToSaveRequest(false)}
                              className="flex-1 py-3 rounded-xl border border-gray-700 text-gray-400 hover:bg-white/5 transition-colors font-semibold"
                           >
                               Pass
                           </button>
                           <button 
                              onClick={() => respondToSaveRequest(true)}
                              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#0BD9E5] to-[#1A6C8C] text-black hover:opacity-90 transition-opacity font-bold"
                           >
                               Accept
                           </button>
                       </div>
                   </motion.div>
               </motion.div>
           )}
       </AnimatePresence>

       {/* WebRTC Camera Modal */}
       <CameraModal 
          isOpen={showCamera} 
          onClose={() => setShowCamera(false)} 
          onCapture={(file) => {
              setSelectedImage(file);
              setSelectedGif(null);
          }} 
       />

    </main>
  );
};

export default ChatPage;
